/**
 * Float32 rotation helpers emulating Carbon's single-precision math
 * (carbon-math Quaternion/Matrix/TransformCoord, used by Ball::GetRotatedVector
 * when dynamical orientation is off). Every intermediate is rounded through
 * Math.fround to reproduce the C++ float pipeline.
 */
const f = Math.fround;

export interface QuatF {
  x: number;
  y: number;
  z: number;
  w: number;
}

/** carbon-math RotationQuaternion(yaw, pitch, roll) — RollPitchYaw convention. */
export function rotationQuaternionYPR(yaw: number, pitch: number, roll: number): QuatF {
  const sy = f(Math.sin(f(f(yaw) / 2)));
  const cy = f(Math.cos(f(f(yaw) / 2)));
  const sp = f(Math.sin(f(f(pitch) / 2)));
  const cp = f(Math.cos(f(f(pitch) / 2)));
  const sr = f(Math.sin(f(f(roll) / 2)));
  const cr = f(Math.cos(f(f(roll) / 2)));
  return {
    x: f(f(sy * cp) * sr + f(cy * sp) * cr),
    y: f(f(sy * cp) * cr - f(cy * sp) * sr),
    z: f(f(cy * cp) * sr - f(sy * sp) * cr),
    w: f(f(cy * cp) * cr + f(sy * sp) * sr),
  };
}

/**
 * Rotate float32 vector [x,y,z] by quaternion q via the carbon-math
 * RotationMatrix + TransformCoord path (row-vector convention).
 */
export function rotateVectorF32(
  v: { x: number; y: number; z: number },
  q: QuatF,
): { x: number; y: number; z: number } {
  const m00 = f(1 - f(2 * f(q.y * q.y + q.z * q.z)));
  const m01 = f(2 * f(q.x * q.y + q.z * q.w));
  const m02 = f(2 * f(q.x * q.z - q.y * q.w));
  const m10 = f(2 * f(q.x * q.y - q.z * q.w));
  const m11 = f(1 - f(2 * f(q.x * q.x + q.z * q.z)));
  const m12 = f(2 * f(q.y * q.z + q.x * q.w));
  const m20 = f(2 * f(q.x * q.z + q.y * q.w));
  const m21 = f(2 * f(q.y * q.z - q.x * q.w));
  const m22 = f(1 - f(2 * f(q.x * q.x + q.y * q.y)));

  const x = f(v.x);
  const y = f(v.y);
  const z = f(v.z);
  return {
    x: f(f(f(f(x * m00) + f(y * m10)) + f(z * m20))),
    y: f(f(f(f(x * m01) + f(y * m11)) + f(z * m21))),
    z: f(f(f(f(x * m02) + f(y * m12)) + f(z * m22))),
  };
}
